import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, ExternalLink, HelpCircle, Loader2, MapPinHouse } from "lucide-react";
import { SectionCard } from "@/components/workflow/common/SectionCard";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapContainer, TileLayer } from "react-leaflet";
import { LocationMarker, forwardGeocode } from "../common/LocationMarker";
import { useToast } from "@/hooks/use-toast";
import type { SiteAddress } from "@/contexts/wizard/types";

interface LocationFormProps {
  generalInfo: any;
  updateGeneralInfo: (updates: any) => void;
  isMapDialogOpen: boolean;
  setIsMapDialogOpen: (open: boolean) => void;
  pendingMapCoords: { lat: number; lng: number } | null;
  setPendingMapCoords: (coords: { lat: number; lng: number } | null) => void;
  latInputValue: string;
  setLatInputValue: (value: string) => void;
  lngInputValue: string;
  setLngInputValue: (value: string) => void;
  coordsFromMapClick: boolean;
  setCoordsFromMapClick: (value: boolean) => void;
  currentSiteIndex: number | null;
  setCurrentSiteIndex: (index: number | null) => void;
  isLocked: boolean;
}

const fieldLabelClassName = "text-sm font-medium leading-5 text-slate-700";
const fieldInputClassName = "h-11 rounded-2xl border-slate-200 bg-slate-50 px-3 text-base text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-0 focus-visible:border-slate-300";
const fieldSelectClassName = "h-11 rounded-2xl border-slate-200 bg-slate-50 px-3 text-base text-slate-900 shadow-none data-[placeholder]:text-slate-400 focus:ring-0";

export function LocationForm({
  generalInfo,
  updateGeneralInfo,
  isMapDialogOpen,
  setIsMapDialogOpen,
  pendingMapCoords,
  setPendingMapCoords,
  latInputValue,
  setLatInputValue,
  lngInputValue,
  setLngInputValue,
  coordsFromMapClick,
  setCoordsFromMapClick,
  currentSiteIndex,
  setCurrentSiteIndex,
  isLocked
}: LocationFormProps) {
  const { toast } = useToast();
  const [suggestions, setSuggestions] = useState<Array<{ lat: number; lng: number; name: string }>>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState<number | null>(null);
  const [openSuggestionsFor, setOpenSuggestionsFor] = useState<number | null>(null);
  const suggestionsRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize siteAddresses if not already set
  useEffect(() => {
    if (!generalInfo.siteAddresses || generalInfo.siteAddresses.length === 0) {
      const initialSite: SiteAddress = {
        id: `site-1`,
        address: "",
        coords: null,
        isPrimary: true
      };
      updateGeneralInfo({
        numberOfSites: generalInfo.numberOfSites || "1",
        siteAddresses: [initialSite]
      });
    }
  }, []);

  // Handle site count changes - adjust array size
  const handleSiteCountChange = (value: string) => {
    const count = value === "5+" ? 5 : parseInt(value);
    const currentSites = generalInfo.siteAddresses || [];

    if (count > currentSites.length) {
      // Add new sites
      const newSites = [...currentSites];
      for (let i = currentSites.length; i < count; i++) {
        newSites.push({
          id: `site-${i + 1}`,
          address: "",
          coords: null,
          isPrimary: i === 0
        });
      }
      updateGeneralInfo({ numberOfSites: value, siteAddresses: newSites });
    } else if (count < currentSites.length) {
      // Remove excess sites
      const trimmedSites = currentSites.slice(0, count);
      updateGeneralInfo({ numberOfSites: value, siteAddresses: trimmedSites });
    } else {
      // Same count, just update the numberOfSites value
      updateGeneralInfo({ numberOfSites: value });
    }
  };

  // Handle address input change for a specific site with autocomplete
  const handleAddressChange = (index: number, address: string) => {
    const updatedSites = [...generalInfo.siteAddresses];
    updatedSites[index] = { ...updatedSites[index], address };
    updateGeneralInfo({ siteAddresses: updatedSites });

    // Trigger address search with debounce
    setOpenSuggestionsFor(index);

    if (suggestionsRef.current) {
      clearTimeout(suggestionsRef.current);
    }

    if (address.trim().length >= 2) {
      setIsSearching(true);
      suggestionsRef.current = setTimeout(async () => {
        const results = await forwardGeocode(address);
        setSuggestions(results);
        setActiveSuggestionIndex(null);
        setIsSearching(false);
      }, 500);
    } else {
      setSuggestions([]);
      setActiveSuggestionIndex(null);
    }
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (siteIndex: number, suggestion: { lat: number; lng: number; name: string }) => {
    const updatedSites = [...generalInfo.siteAddresses];
    updatedSites[siteIndex] = {
      ...updatedSites[siteIndex],
      address: suggestion.name,
      coords: { lat: suggestion.lat, lng: suggestion.lng }
    };
    updateGeneralInfo({ siteAddresses: updatedSites });
    setPendingMapCoords({ lat: suggestion.lat, lng: suggestion.lng });
    setLatInputValue(suggestion.lat.toString());
    setLngInputValue(suggestion.lng.toString());
    toast({
      title: "Location selected",
      description: suggestion.name
    });
    setSuggestions([]);
    setOpenSuggestionsFor(null);
  };

  // Open map dialog for specific site
  const openMapForSite = (index: number) => {
    setCurrentSiteIndex(index);
    const site = generalInfo.siteAddresses[index];
    if (site?.coords) {
      setPendingMapCoords(site.coords);
      setLatInputValue(site.coords.lat.toString());
      setLngInputValue(site.coords.lng.toString());
    } else {
      setPendingMapCoords(null);
      setLatInputValue("");
      setLngInputValue("");
    }
    setIsMapDialogOpen(true);
  };

  // Save map coordinates for current site
  const saveMapCoordinates = () => {
    if (pendingMapCoords && currentSiteIndex !== null) {
      const updatedSites = [...generalInfo.siteAddresses];
      updatedSites[currentSiteIndex] = {
        ...updatedSites[currentSiteIndex],
        coords: pendingMapCoords
      };
      updateGeneralInfo({ siteAddresses: updatedSites });
      toast({ title: "Location saved", description: `Location pinned for ${updatedSites[currentSiteIndex].isPrimary ? "Primary Site" : `Site ${currentSiteIndex + 1}`}` });
    }
    setIsMapDialogOpen(false);
    setCurrentSiteIndex(null);
  };

  // Handle location name resolution from map
  const handleLocationResolved = (locationName: string) => {
    if (currentSiteIndex !== null) {
      const updatedSites = [...generalInfo.siteAddresses];
      updatedSites[currentSiteIndex] = {
        ...updatedSites[currentSiteIndex],
        address: locationName
      };
      updateGeneralInfo({ siteAddresses: updatedSites });
      toast({
        title: "Location detected",
        description: locationName
      });
    }
  };

  useEffect(() => {
    if (coordsFromMapClick && pendingMapCoords) {
      setLatInputValue(pendingMapCoords.lat.toString());
      setLngInputValue(pendingMapCoords.lng.toString());
      setCoordsFromMapClick(false);
    }
  }, [pendingMapCoords, coordsFromMapClick, setLatInputValue, setLngInputValue, setCoordsFromMapClick]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-suggestions-container]')) {
        setOpenSuggestionsFor(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const siteCount = generalInfo.numberOfSites === "5+" ? 5 : parseInt(generalInfo.numberOfSites || "1");
  const sites = generalInfo.siteAddresses || [];

  const header = (
    <div className="flex min-w-0 items-center gap-2">
      <MapPinHouse className="h-5 w-5 flex-shrink-0 text-[#00444C]" />
      <h3 className="text-lg font-bold leading-7 text-slate-900">{"Location & Sites"}</h3>
      <HelpCircle className="h-4 w-4 flex-shrink-0 text-slate-600" />
    </div>
  );

  return (
    <>
      <SectionCard header={header} className="wizard-card-overflowable">
        <div className="space-y-6">

          {/* Number of Sites Selector */}
          <div className="space-y-2">
            <Label className={fieldLabelClassName}>
              {"Number of Sites/Locations"} <span className="text-red-500">*</span>
            </Label>
            <Select
              value={generalInfo.numberOfSites || "1"}
              onValueChange={handleSiteCountChange}
              disabled={isLocked}
            >
              <SelectTrigger data-testid="select-sites" className={`w-full md:w-72 ${fieldSelectClassName}`}>
                <SelectValue placeholder={"Select number of sites"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">{"1 Site"}</SelectItem>
                <SelectItem value="2">{"2 Sites"}</SelectItem>
                <SelectItem value="3">{"3 Sites"}</SelectItem>
                <SelectItem value="4">{"4 Sites"}</SelectItem>
                <SelectItem value="5+">{"5+ Sites (Max 5)"}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Dynamic Site Address Fields */}
          <div className="space-y-4">
            {Array.from({ length: siteCount }).map((_, index) => {
              const site = sites[index];
              const isPrimary = index === 0;

              return (
                <div key={`site-${index}`} className="space-y-3 rounded-3xl border border-slate-200 bg-slate-50 p-4">
                  <div className="space-y-2">
                    <Label className={fieldLabelClassName}>
                      {isPrimary ? "Primary Site Address" : `Site ${index + 1} Address`}
                      {isPrimary && <span className="text-red-500"> *</span>}
                    </Label>
                    <div className="flex gap-2 relative" data-suggestions-container>
                      <div className="flex-1 relative">
                        <Input
                          placeholder={isPrimary ? "e.g. Lilongwe, Malawi" : "e.g. Blantyre, Malawi"}
                          value={site?.address || ""}
                          onChange={(e) => handleAddressChange(index, e.target.value)}
                          onFocus={() => setOpenSuggestionsFor(index)}
                          data-testid={`input-site-address-${index}`}
                          disabled={isLocked}
                          autoComplete="off"
                          className={fieldInputClassName}
                        />
                        {isSearching && openSuggestionsFor === index && (
                          <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                        )}

                        {/* Autocomplete suggestions dropdown */}
                        {openSuggestionsFor === index && suggestions.length > 0 && (
                          <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-lg">
                            {suggestions.map((suggestion, idx) => (
                              <button
                                key={idx}
                                type="button"
                                className={`w-full px-4 py-2 text-left text-sm transition-colors hover:bg-[#00444C]/5 ${activeSuggestionIndex === idx ? 'bg-[#00444C]/10' : ''
                                  }`}
                                onClick={() => handleSuggestionSelect(index, suggestion)}
                                onMouseEnter={() => setActiveSuggestionIndex(idx)}
                              >
                                <div className="font-medium text-gray-900">{suggestion.name.split(',')[0]}</div>
                                <div className="text-xs text-gray-500">{suggestion.name}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-11 flex-shrink-0 rounded-2xl border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 shadow-none hover:bg-slate-100"
                        onClick={() => openMapForSite(index)}
                        data-testid={`button-map-site-${index}`}
                        disabled={isLocked}
                      >
                        <MapPinHouse className="h-4 w-4" /> {"Map"}
                      </Button>
                    </div>
                    {site?.coords && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                        <span>{"Location pinned"}: {site.coords.lat.toFixed(4)}, {site.coords.lng.toFixed(4)}</span>
                        <a
                          href={`https://www.google.com/maps?q=${site.coords.lat},${site.coords.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline flex items-center gap-1"
                        >
                          <ExternalLink className="h-3 w-3" /> {"View in Google Maps"}
                        </a>
                      </div>
                    )}
                    {!site?.coords && <p className="text-xs text-muted-foreground">{"Click Map to pin exact location on the map"}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </SectionCard>

      <Dialog open={isMapDialogOpen} onOpenChange={(open) => {
        setIsMapDialogOpen(open);
        if (!open) setCurrentSiteIndex(null);
      }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {"Select Location"} {currentSiteIndex !== null && (currentSiteIndex === 0 ? "(Primary Site)" : `(Site ${currentSiteIndex + 1})`)}
            </DialogTitle>
            <DialogDescription>
              {"Click on the map to pin your business location, or enter coordinates manually below."}
            </DialogDescription>
          </DialogHeader>
          <div className="h-[350px] w-full rounded-md overflow-hidden border">
            <MapContainer
              center={pendingMapCoords ? [pendingMapCoords.lat, pendingMapCoords.lng] : [-13.9626, 33.7741]}
              zoom={pendingMapCoords ? 12 : 6}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <LocationMarker
                position={pendingMapCoords}
                setPosition={setPendingMapCoords}
                onMapClick={() => setCoordsFromMapClick(true)}
                onLocationResolved={handleLocationResolved}
              />
            </MapContainer>
          </div>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{"Or enter coordinates manually:"}</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{"Latitude (-90 to 90)"}</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder={"e.g. -13.9626"}
                  value={latInputValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    setLatInputValue(value);
                    const lat = parseFloat(value);
                    if (!isNaN(lat) && lat >= -90 && lat <= 90) {
                      const lng = pendingMapCoords?.lng ?? (lngInputValue ? parseFloat(lngInputValue) : 33.7741);
                      setPendingMapCoords({ lat, lng });
                    }
                  }}
                  data-testid="input-latitude"
                  disabled={isLocked}
                />
              </div>
              <div className="space-y-2">
                <Label>{"Longitude (-180 to 180)"}</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder={"e.g. 33.7741"}
                  value={lngInputValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    setLngInputValue(value);
                    const lng = parseFloat(value);
                    if (!isNaN(lng) && lng >= -180 && lng <= 180) {
                      const lat = pendingMapCoords?.lat ?? (latInputValue ? parseFloat(latInputValue) : -13.9626);
                      setPendingMapCoords({ lat, lng });
                    }
                  }}
                  data-testid="input-longitude"
                  disabled={isLocked}
                />
              </div>
            </div>
            {pendingMapCoords && (
              <a
                href={`https://www.google.com/maps?q=${pendingMapCoords.lat},${pendingMapCoords.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                <ExternalLink className="h-4 w-4" /> {"Preview in Google Maps"}
              </a>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsMapDialogOpen(false);
              setCurrentSiteIndex(null);
            }} disabled={isLocked}>
              {"Cancel"}
            </Button>
            <Button
              onClick={saveMapCoordinates}
              disabled={!pendingMapCoords}
              data-testid="button-save-location"
            >
              {"Save Location"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
